import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, RefreshCw, DollarSign, Star, Eye } from 'lucide-react';

const GRAD_BLUE_PURPLE = 'linear-gradient(135deg,#4F8EF7,#7C3AED)';
const GRAD_GREEN = 'linear-gradient(135deg,#10B981,#059669)';

export default function AdminTutors() {
  const [tab, setTab] = useState('tutors');
  const [tutors, setTutors] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [stats, setStats] = useState({});
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  useEffect(() => { load(); }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'tutors') {
        const { data } = await supabase
          .from('tutor_profiles')
          .select('*')
          .order('created_at', { ascending: false });
        const all = data || [];
        setTutors(all);
        setStats({
          total: all.length,
          active: all.filter(t => t.is_active).length,
          pending: all.filter(t => !t.is_active).length,
          verified: all.filter(t => t.is_verified).length,
          totalMeetings: all.reduce((s, t) => s + (t.total_meetings || 0), 0),
        });
      } else if (tab === 'bookings') {
        const { data } = await supabase
          .from('tutor_bookings')
          .select('*, tutor_profiles(full_name, country)')
          .order('created_at', { ascending: false })
          .limit(200);
        setBookings(data || []);
      } else if (tab === 'payments') {
        const { data } = await supabase
          .from('tutor_bookings')
          .select('id, tutor_id, booking_date, subject, session_type, amount_charged, currency, teacher_earning, status, payment_status, actual_participants, tutor_profiles(full_name, bank_name, account_title, account_number, iban)')
          .eq('status', 'completed')
          .order('booking_date', { ascending: false });
        setPayments(data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const approveTutor = async (id, approve) => {
    const { error } = await supabase
      .from('tutor_profiles')
      .update({ is_active: approve, is_verified: approve })
      .eq('id', id);
    if (!error) {
      showToast(approve ? '✅ Tutor approved and now visible to students' : '❌ Tutor deactivated');
      load();
    }
  };

  const markPaid = async (bookingId) => {
    const { error } = await supabase
      .from('tutor_bookings')
      .update({ payment_status: 'paid', paid_at: new Date().toISOString() })
      .eq('id', bookingId);
    if (!error) {
      showToast('💰 Payment marked as paid');
      load();
    }
  };

  const filteredTutors = tutors.filter(t =>
    !search ||
    (t.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (t.subjects || []).some(s => s.toLowerCase().includes(search.toLowerCase()))
  );

  const pendingPayments = payments.filter(p => p.payment_status !== 'paid');
  const totalPendingPKR = pendingPayments.filter(p => p.currency === 'PKR').reduce((s, p) => s + (p.teacher_earning || 0), 0);
  const totalPendingUSD = pendingPayments.filter(p => p.currency === 'USD').reduce((s, p) => s + (p.teacher_earning || 0), 0);

  const TABS = [
    { id: 'tutors', label: '👨‍🏫 Tutors' },
    { id: 'bookings', label: '📅 Bookings' },
    { id: 'payments', label: '💰 Payments' },
  ];

  const STATUS_COLORS = {
    pending: '#F59E0B',
    accepted: '#10B981',
    rejected: '#EF4444',
    completed: '#22D3EE',
    in_progress: '#4F8EF7',
    no_show: '#EF4444',
    cancelled: '#94A3B8',
  };

  const avatarStyle = {
    width: 36, height: 36, borderRadius: '50%',
    background: GRAD_BLUE_PURPLE,
    display: 'flex', alignItems: 'center',
    justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: 14,
    overflow: 'hidden', flexShrink: 0,
  };

  const thStyle = {
    padding: '11px 14px', textAlign: 'left',
    fontSize: 10, fontWeight: 700,
    color: '#94A3B8', letterSpacing: 0.5,
    borderBottom: '1px solid #F1F5F9',
    background: '#F8FAFF',
  };

  const tdStyle = { padding: '10px 12px' };

  const chip = (text, bg, color) => (
    <span style={{ padding: '3px 8px', borderRadius: 5, background: bg, color, fontSize: 10, fontWeight: 700 }}>
      {text}
    </span>
  );

  const subjectChip = (s, i) => (
    <span key={i} style={{ padding: '2px 7px', borderRadius: 4, background: 'rgba(79,142,247,0.1)', color: '#4F8EF7', fontSize: 10, fontWeight: 600, marginRight: 3, marginBottom: 3, display: 'inline-block' }}>
      {s}
    </span>
  );

  return (
    <div style={{ padding: 24, minHeight: '100vh', background: '#F8FAFF' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: toast.startsWith('❌') ? '#FFF1F2' : '#F0FDF4',
          border: `1px solid ${toast.startsWith('❌') ? '#FECDD3' : '#BBF7D0'}`,
          borderRadius: 12, padding: '12px 20px',
          fontSize: 13, fontWeight: 600,
          color: toast.startsWith('❌') ? '#E11D48' : '#16A34A',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>{toast}</div>
      )}

      {/* Page title */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1E293B', margin: 0 }}>Tutor Management</h1>
        <p style={{ color: '#94A3B8', fontSize: 14, marginTop: 4 }}>Approve tutors, monitor bookings, manage payments</p>
      </div>

      {/* Stats row */}
      {tab === 'tutors' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { k: 'total', l: 'Total Tutors', i: '👨‍🏫', c: '#4F8EF7' },
            { k: 'active', l: 'Active', i: '✅', c: '#10B981' },
            { k: 'pending', l: 'Pending Approval', i: '⏳', c: '#F59E0B' },
            { k: 'verified', l: 'Verified', i: '🛡', c: '#7C3AED' },
            { k: 'totalMeetings', l: 'Total Sessions', i: '🎓', c: '#22D3EE' },
          ].map(({ k, l, i, c }) => (
            <div key={k} style={{ background: '#fff', borderRadius: 12, padding: '16px 14px', border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{i}</span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{stats[k] ?? 0}</div>
                <div style={{ fontSize: 10, color: '#94A3B8' }}>{l}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending payments banner */}
      {tab === 'payments' && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
          <DollarSign size={20} color="#F59E0B" />
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#92400E' }}>Pending Payments Due</div>
            <div style={{ fontSize: 13, color: '#78350F', marginTop: 2 }}>
              PKR {totalPendingPKR.toLocaleString()} · ${totalPendingUSD.toFixed(2)} USD due to {new Set(pendingPayments.map(p => p.tutor_id)).size} tutors
            </div>
          </div>
        </div>
      )}

      {/* Tabs + search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', borderRadius: 10,
            border: tab === t.id ? '2px solid #4F8EF7' : '1.5px solid #E2E8F0',
            background: tab === t.id ? '#EFF6FF' : '#fff',
            color: tab === t.id ? '#4F8EF7' : '#64748B',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>{t.label}</button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {tab === 'tutors' && (
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tutors..."
                style={{ padding: '8px 12px 8px 30px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, outline: 'none', width: 200 }}
              />
            </div>
          )}
          <button onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E2E8F0', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#64748B' }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ══════════════ TUTORS TAB ══════════════ */}
      {tab === 'tutors' && (
        <div style={{ display: 'flex', gap: 20 }}>

          {/* Table */}
          <div style={{ flex: 1, background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            {loading ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
            ) : filteredTutors.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>👨‍🏫</div>
                <div>No tutors found</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Tutor', 'Subjects', 'Rating', 'Sessions', 'Status', 'Actions'].map(h => (
                      <th key={h} style={thStyle}>{h.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTutors.map(t => (
                    <tr key={t.id} style={{ borderBottom: '1px solid #F1F5F9', background: selectedTutor?.id === t.id ? 'rgba(79,142,247,0.04)' : 'transparent' }}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={avatarStyle}>
                            {t.avatar_url
                              ? <img src={t.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : (t.full_name || 'T')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>{t.full_name}</div>
                            <div style={{ fontSize: 11, color: '#94A3B8' }}>{t.years_experience} yrs · {t.city || t.country}</div>
                          </div>
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: 180 }}>
                          {(t.subjects || []).slice(0, 3).map((s, i) => subjectChip(s, i))}
                          {(t.subjects || []).length > 3 && <span style={{ fontSize: 10, color: '#94A3B8' }}>+{t.subjects.length - 3}</span>}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Star size={12} fill="#F59E0B" color="#F59E0B" />
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1E293B' }}>{(t.rating_avg || 0).toFixed(1)}</span>
                          <span style={{ fontSize: 11, color: '#94A3B8' }}>({t.rating_count || 0})</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{t.total_meetings || 0}</td>
                      <td style={tdStyle}>
                        {t.is_active
                          ? chip('Active', '#DCFCE7', '#16A34A')
                          : chip('Pending', '#FEF9C3', '#A16207')}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setSelectedTutor(selectedTutor?.id === t.id ? null : t)} style={{ padding: '5px 9px', borderRadius: 6, border: '1px solid #E2E8F0', background: '#F8FAFF', color: '#64748B', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Eye size={11} /> View
                          </button>
                          {!t.is_active
                            ? <button onClick={() => approveTutor(t.id, true)} style={{ padding: '5px 9px', borderRadius: 6, border: 'none', background: '#DCFCE7', color: '#16A34A', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✅ Approve</button>
                            : <button onClick={() => approveTutor(t.id, false)} style={{ padding: '5px 9px', borderRadius: 6, border: 'none', background: '#FEE2E2', color: '#DC2626', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>❌ Deactivate</button>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Detail panel */}
          {selectedTutor && (
            <div style={{ width: 320, flexShrink: 0, background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 20, overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: '#1E293B' }}>Tutor Detail</div>
                <button onClick={() => setSelectedTutor(null)} style={{ border: 'none', background: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: 20 }}>×</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: GRAD_BLUE_PURPLE, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 22, overflow: 'hidden' }}>
                  {selectedTutor.avatar_url
                    ? <img src={selectedTutor.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (selectedTutor.full_name || 'T')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#1E293B' }}>{selectedTutor.full_name}</div>
                  <div style={{ fontSize: 12, color: '#94A3B8' }}>{selectedTutor.tagline}</div>
                </div>
              </div>

              {[
                ['Education', selectedTutor.education],
                ['University', selectedTutor.university],
                ['Experience', selectedTutor.years_experience ? `${selectedTutor.years_experience} years` : null],
                ['Location', [selectedTutor.city, selectedTutor.country].filter(Boolean).join(', ')],
                ['PKR — 1-on-1/hr', selectedTutor.hourly_rate_pkr ? `PKR ${selectedTutor.hourly_rate_pkr}` : null],
                ['PKR — Group/hr', selectedTutor.group_rate_pkr ? `PKR ${selectedTutor.group_rate_pkr}` : null],
                ['USD — 1-on-1/hr', selectedTutor.hourly_rate_usd ? `$${selectedTutor.hourly_rate_usd}` : null],
                ['USD — Group/hr', selectedTutor.group_rate_usd ? `$${selectedTutor.group_rate_usd}` : null],
                ['Total Sessions', selectedTutor.total_meetings],
                ['Rating', `${(selectedTutor.rating_avg || 0).toFixed(1)} ⭐ (${selectedTutor.rating_count || 0})`],
              ].filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '5px 0', borderBottom: '1px solid #F1F5F9' }}>
                  <span style={{ color: '#94A3B8' }}>{k}</span>
                  <span style={{ color: '#1E293B', fontWeight: 600, maxWidth: 160, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
                </div>
              ))}

              {selectedTutor.subjects?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 6 }}>SUBJECTS</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selectedTutor.subjects.map((s, i) => subjectChip(s, i))}
                  </div>
                </div>
              )}

              {selectedTutor.bank_name && (
                <div style={{ marginTop: 14, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#A16207', marginBottom: 8 }}>🏦 Bank Details</div>
                  {[
                    ['Bank', selectedTutor.bank_name],
                    ['Account Title', selectedTutor.account_title],
                    ['Account No.', selectedTutor.account_number],
                    ['IBAN', selectedTutor.iban],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0' }}>
                      <span style={{ color: '#78350F' }}>{k}</span>
                      <span style={{ color: '#92400E', fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                {!selectedTutor.is_active
                  ? <button onClick={() => approveTutor(selectedTutor.id, true)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: GRAD_GREEN, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>✅ Approve Tutor</button>
                  : <button onClick={() => approveTutor(selectedTutor.id, false)} style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#EF4444', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>❌ Deactivate</button>
                }
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════ BOOKINGS TAB ══════════════ */}
      {tab === 'bookings' && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
          ) : bookings.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
              <div>No bookings yet</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  {['Tutor', 'Subject', 'Date / Time', 'Type', 'Amount', 'Participants', 'Status', 'Payment'].map(h => (
                    <th key={h} style={thStyle}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => {
                  const sc = STATUS_COLORS[b.status] || '#94A3B8';
                  const isGroup = b.session_type === 'group';
                  const isUSD = b.currency === 'USD';
                  return (
                    <tr key={b.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1E293B' }}>{b.tutor_profiles?.full_name}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>{b.tutor_profiles?.country}</div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, color: '#475569' }}>{b.subject}</td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: 12, color: '#1E293B', fontWeight: 600 }}>{b.booking_date}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{b.start_time}–{b.end_time}</div>
                      </td>
                      <td style={tdStyle}>
                        {chip(isGroup ? '👥 Group' : '👤 1-on-1', isGroup ? 'rgba(124,58,237,0.1)' : 'rgba(79,142,247,0.1)', isGroup ? '#7C3AED' : '#4F8EF7')}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                          {isUSD ? `$${(b.amount_charged || 0).toFixed(0)}` : `PKR ${(b.amount_charged || 0).toFixed(0)}`}
                        </div>
                        <div style={{ fontSize: 10, color: '#10B981' }}>
                          Teacher: {isUSD ? `$${(b.teacher_earning || 0).toFixed(0)}` : `PKR ${(b.teacher_earning || 0).toFixed(0)}`}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#1E293B' }}>
                        {b.actual_participants || '—'}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ padding: '3px 8px', borderRadius: 5, background: `${sc}20`, color: sc, fontSize: 10, fontWeight: 700 }}>
                          {(b.status || '').toUpperCase()}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {b.payment_status === 'paid'
                          ? chip('✅ PAID', '#DCFCE7', '#16A34A')
                          : b.payment_status === 'due'
                            ? chip('⏳ DUE', '#FEF9C3', '#A16207')
                            : chip('⏸ PENDING', '#F1F5F9', '#94A3B8')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ══════════════ PAYMENTS TAB ══════════════ */}
      {tab === 'payments' && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>Loading...</div>
          ) : payments.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>💰</div>
              <div>No completed sessions yet</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
              <thead>
                <tr>
                  {['Tutor', 'Session', 'Date', 'Students', 'Charged', 'Teacher Cut (40%)', 'Bank Details', 'Status', ''].map(h => (
                    <th key={h} style={thStyle}>{h.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => {
                  const isUSD = p.currency === 'USD';
                  const isPaid = p.payment_status === 'paid';
                  const isDue = p.payment_status === 'due';
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #F1F5F9', background: isPaid ? 'rgba(16,185,129,0.02)' : isDue ? 'rgba(245,158,11,0.03)' : 'transparent' }}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1E293B' }}>{p.tutor_profiles?.full_name}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: 12, color: '#475569' }}>{p.subject}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.session_type === 'group' ? '👥 Group' : '👤 1-on-1'}</div>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, color: '#475569' }}>{p.booking_date}</td>
                      <td style={{ ...tdStyle, fontSize: 12, fontWeight: 700, color: '#1E293B', textAlign: 'center' }}>{p.actual_participants || 1}</td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1E293B' }}>
                          {isUSD ? `$${(p.amount_charged || 0).toFixed(0)}` : `PKR ${(p.amount_charged || 0).toFixed(0)}`}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 800, fontSize: 15, color: '#10B981' }}>
                          {isUSD ? `$${(p.teacher_earning || 0).toFixed(0)}` : `PKR ${(p.teacher_earning || 0).toFixed(0)}`}
                        </div>
                      </td>
                      <td style={tdStyle}>
                        {p.tutor_profiles?.bank_name ? (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#1E293B' }}>{p.tutor_profiles.bank_name}</div>
                            <div style={{ fontSize: 10, color: '#94A3B8' }}>{p.tutor_profiles.account_title}</div>
                            <div style={{ fontSize: 10, color: '#64748B', fontFamily: 'monospace' }}>{p.tutor_profiles.account_number}</div>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>⚠️ No bank details</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {isPaid
                          ? chip('✅ PAID', '#DCFCE7', '#16A34A')
                          : isDue
                            ? chip('⏳ DUE', '#FEF9C3', '#A16207')
                            : chip('⏸ PENDING', '#F1F5F9', '#64748B')}
                      </td>
                      <td style={tdStyle}>
                        {!isPaid && (
                          <button onClick={() => markPaid(p.id)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: GRAD_GREEN, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            💰 Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}